import { useMemo, useState } from 'react';
import { MetaPage, MetaUser } from '../types/meta.types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    Plus,
    RefreshCw,
    XCircle,
    User,
    Layers,
    FolderTree,
} from 'lucide-react';
import ProjectInlineSelector from './ProjectInlineSelector';
import ManagePagesProjectsDialog from './ManagePagesProjectsDialog';

interface AccountsListProps {
    authentications: MetaUser[];
    /** Páginas do usuário — usadas pra expandir overrides por conta. */
    pages: MetaPage[];
    /** Projetos do user atual (lista pra dropdown). */
    projects: { id: number; name: string }[];
    onSync: (auth: MetaUser) => void;
    onRevoke: (auth: MetaUser) => void;
    onConnectAccount: () => void;
    /** Vincula a conta inteira a um projeto. project_id=null = sem vínculo. */
    onUpdateAuthProject: (authId: number, projectId: number | null) => void;
    /** Override por página. project_id=null = herda da conta. */
    onUpdatePageProject: (pageId: number, projectId: number | null) => void;
}

interface GroupedAuth {
    name: string;
    picture_url: string | null;
    /** Todas as authentications dessa pessoa (1 por app conectado). */
    auths: MetaUser[];
}

export default function AccountsList({
    authentications,
    pages,
    projects,
    onSync,
    onRevoke,
    onConnectAccount,
    onUpdateAuthProject,
    onUpdatePageProject,
}: AccountsListProps) {
    const [groupByName, setGroupByName] = useState(false);
    /** Conta cujo modal de "gerenciar pages" está aberto (null = fechado). */
    const [managingAuth, setManagingAuth] = useState<MetaUser | null>(null);

    // Pages indexadas por oauth_account.id. Usa accessible_meta_user_ids
    // (master + page_tokens ativos) pra incluir dual-app — uma page com tokens
    // em 2 contas conta pras DUAS. Fallback pra meta_user_id (master) se o
    // backend ainda não enriqueceu.
    const pagesByAuthId = useMemo(() => {
        const map = new Map<number, MetaPage[]>();
        pages.forEach(p => {
            const ids = p.accessible_meta_user_ids && p.accessible_meta_user_ids.length > 0
                ? p.accessible_meta_user_ids
                : (p.meta_user_id ? [p.meta_user_id] : []);
            ids.forEach(authId => {
                const arr = map.get(authId) || [];
                arr.push(p);
                map.set(authId, arr);
            });
        });
        // Ordena por nome dentro de cada conta.
        map.forEach(arr => arr.sort((a, b) => (a.page_name || '').localeCompare(b.page_name || '', 'pt-BR')));
        return map;
    }, [pages]);

    const projectNameById = useMemo(() => {
        const m = new Map<number, string>();
        projects.forEach(p => m.set(p.id, p.name));
        return m;
    }, [projects]);

    // Quando agrupado por nome, cada grupo combina N authentications da mesma pessoa.
    const groupedAuths = useMemo<GroupedAuth[]>(() => {
        const map = new Map<string, GroupedAuth>();
        authentications.forEach(a => {
            const key = a.meta_user_name || '(sem nome)';
            const existing = map.get(key);
            if (existing) {
                existing.auths.push(a);
                if (!existing.picture_url && a.picture_url) existing.picture_url = a.picture_url;
            } else {
                map.set(key, { name: key, picture_url: a.picture_url, auths: [a] });
            }
        });
        return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
    }, [authentications]);

    return (
        <div className="space-y-12">
            {/* Authentications Section */}
            <div className="space-y-6">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">Perfis Conectados</h1>
                        <p className="mt-2 text-gray-600">Gerencie as contas do Facebook integradas ao sistema.</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <label className="flex items-center gap-2 cursor-pointer select-none">
                            <div
                                onClick={() => setGroupByName(v => !v)}
                                className={`relative w-9 h-5 rounded-full transition-colors cursor-pointer ${groupByName ? 'bg-indigo-500' : 'bg-gray-200'}`}
                            >
                                <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${groupByName ? 'translate-x-4' : 'translate-x-0'}`} />
                            </div>
                            <div className="flex items-center gap-1.5">
                                <Layers className={`h-3.5 w-3.5 ${groupByName ? 'text-indigo-500' : 'text-gray-400'}`} />
                                <span className="text-sm text-gray-600 whitespace-nowrap">Agrupar por nome</span>
                            </div>
                        </label>
                        <Button
                            onClick={onConnectAccount}
                            className="bg-[#475162] hover:bg-[#3a4151] text-white font-medium px-6 py-2.5 rounded-xl border border-[#475162] transition-all duration-300 hover:shadow-lg"
                        >
                            <Plus className="mr-2 h-4 w-4" />
                            Conectar Conta
                        </Button>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow border border-gray-100 overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Perfil</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">App Utilizado</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Projeto</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {authentications.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="px-6 py-12 text-center">
                                        <div className="flex flex-col items-center justify-center">
                                            <User className="h-10 w-10 text-gray-300 mb-3" />
                                            <p className="text-gray-500 text-sm">Nenhuma conta conectada.</p>
                                        </div>
                                    </td>
                                </tr>
                            )}

                            {/* Modo Lista — 1 row por auth */}
                            {!groupByName && authentications.map((auth) => {
                                const accountPages = pagesByAuthId.get(auth.id) || [];
                                return (
                                    <tr key={auth.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center gap-3">
                                                {auth.picture_url ? (
                                                    <img src={auth.picture_url} alt={auth.meta_user_name} className="h-10 w-10 rounded-full border border-gray-100 object-cover" />
                                                ) : (
                                                    <div className="h-10 w-10 rounded-full bg-blue-50 flex items-center justify-center border border-blue-100">
                                                        <User className="h-5 w-5 text-blue-600" />
                                                    </div>
                                                )}
                                                <div>
                                                    <p className="text-sm font-semibold text-gray-900 leading-tight">{auth.meta_user_name}</p>
                                                    <p className="text-xs text-gray-500 mt-0.5">ID: {auth.meta_user_id}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <Badge variant="outline" className="font-medium bg-gray-50">
                                                {auth.app_name}
                                            </Badge>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <ProjectInlineSelector
                                                value={auth.project_id}
                                                onChange={(pid) => onUpdateAuthProject(auth.id, pid)}
                                                projects={projects}
                                                noneLabel="Sem projeto"
                                            />
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {auth.is_active ? (
                                                <Badge variant="success">Ativo</Badge>
                                            ) : (
                                                <Badge variant="destructive">Expirado</Badge>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right">
                                            <div className="flex justify-end gap-2">
                                                {accountPages.length > 0 && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => setManagingAuth(auth)}
                                                        title={`Gerenciar projeto por página (${accountPages.length})`}
                                                        className="h-9 px-2 text-indigo-600 hover:bg-indigo-50 gap-1"
                                                    >
                                                        <FolderTree className="h-4 w-4" />
                                                        <span className="text-xs font-medium">{accountPages.length}</span>
                                                    </Button>
                                                )}
                                                <Button variant="ghost" size="sm" onClick={() => onSync(auth)} title="Sincronizar Páginas" className="h-9 w-9 p-0 text-blue-600 hover:bg-blue-50">
                                                    <RefreshCw className="h-4 w-4" />
                                                </Button>
                                                <Button variant="ghost" size="sm" onClick={() => onRevoke(auth)} title="Remover Conta" className="h-9 w-9 p-0 text-red-600 hover:bg-red-50">
                                                    <XCircle className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}

                            {/* Modo Agrupado — 1 row por nome de perfil */}
                            {groupByName && groupedAuths.map((group) => {
                                const anyActive = group.auths.some(a => a.is_active);
                                const appNames = group.auths.map(a => a.app_name).join(', ');
                                const firstAuth = group.auths[0];
                                return (
                                    <tr key={group.name} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center gap-3">
                                                {group.picture_url ? (
                                                    <img src={group.picture_url} alt={group.name} className="h-10 w-10 rounded-full border border-gray-100 object-cover" />
                                                ) : (
                                                    <div className="h-10 w-10 rounded-full bg-indigo-50 flex items-center justify-center border border-indigo-100">
                                                        <User className="h-5 w-5 text-indigo-600" />
                                                    </div>
                                                )}
                                                <div>
                                                    <p className="text-sm font-semibold text-gray-900 leading-tight">{group.name}</p>
                                                    <p className="text-xs text-gray-500 mt-0.5">
                                                        {group.auths.length === 1
                                                            ? `ID: ${firstAuth.meta_user_id}`
                                                            : `${group.auths.length} contas Meta`}
                                                    </p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {group.auths.length === 1 ? (
                                                <Badge variant="outline" className="font-medium bg-gray-50">
                                                    {firstAuth.app_name}
                                                </Badge>
                                            ) : (
                                                <Badge
                                                    variant="outline"
                                                    className="font-medium text-indigo-700 border-indigo-200 bg-indigo-50/50 flex items-center gap-1 w-fit"
                                                    title={appNames}
                                                >
                                                    <Layers className="h-3 w-3" />
                                                    em {group.auths.length} apps
                                                </Badge>
                                            )}
                                            {group.auths.length > 1 && (
                                                <p className="text-[10px] text-gray-500 mt-1 max-w-[200px] truncate" title={appNames}>
                                                    {appNames}
                                                </p>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {(() => {
                                                // Modo agrupado: se todas as auths apontam pro mesmo projeto, mostra
                                                // editável. Senão, indica "vários projetos" e desabilita.
                                                const distinct = new Set(group.auths.map(a => a.project_id ?? -1));
                                                if (distinct.size > 1) {
                                                    return (
                                                        <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-200 bg-amber-50">
                                                            vários · edite por conta
                                                        </Badge>
                                                    );
                                                }
                                                const singleVal = group.auths[0].project_id;
                                                return (
                                                    <ProjectInlineSelector
                                                        value={singleVal}
                                                        onChange={(pid) => group.auths.forEach(a => onUpdateAuthProject(a.id, pid))}
                                                        projects={projects}
                                                        noneLabel="Sem projeto"
                                                    />
                                                );
                                            })()}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {anyActive ? (
                                                <Badge variant="success">Ativo</Badge>
                                            ) : (
                                                <Badge variant="destructive">Expirado</Badge>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right">
                                            <div className="flex justify-end gap-2">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => group.auths.forEach(a => onSync(a))}
                                                    title={`Sincronizar páginas (${group.auths.length} conta${group.auths.length > 1 ? 's' : ''})`}
                                                    className="h-9 w-9 p-0 text-blue-600 hover:bg-blue-50"
                                                >
                                                    <RefreshCw className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => onRevoke(firstAuth)}
                                                    title={
                                                        group.auths.length > 1
                                                            ? `Remover apenas a conexão "${firstAuth.app_name}" (abre seletor)`
                                                            : 'Remover Conta'
                                                    }
                                                    className="h-9 w-9 p-0 text-red-600 hover:bg-red-50"
                                                >
                                                    <XCircle className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Seção "Configurações de API" foi movida pra aba "Apps" (sidebar
                Conexões → Apps). Manter aqui duplicava CRUD de Meta Apps. */}

            {/* Modal: gerenciar projeto por página da conta selecionada */}
            <ManagePagesProjectsDialog
                open={!!managingAuth}
                onOpenChange={(o) => { if (!o) setManagingAuth(null); }}
                auth={managingAuth}
                pages={managingAuth ? (pagesByAuthId.get(managingAuth.id) || []) : []}
                projects={projects}
                accountProjectName={managingAuth?.project_id != null ? (projectNameById.get(managingAuth.project_id) || null) : null}
                onUpdatePageProject={onUpdatePageProject}
            />
        </div>
    );
}
